if (!customElements.get('pups-sticky-add-to-cart')) {
  customElements.define(
    'pups-sticky-add-to-cart',
    class PupsStickyAddToCart extends HTMLElement {
      connectedCallback() {
        if (this.parentElement !== document.body) {
          document
            .querySelectorAll(
              `body > pups-sticky-add-to-cart[data-section-id="${CSS.escape(this.dataset.sectionId || '')}"]`,
            )
            .forEach((stickyBar) => stickyBar.remove());
          document.body.append(this);
          return;
        }

        this.sourceButton = document.getElementById(this.dataset.sourceButton);
        this.productForm = document.getElementById(this.dataset.productForm);
        this.productInfo = this.sourceButton?.closest('product-info');
        this.button = this.querySelector('.pups-sticky-atc__button');
        this.label = this.button?.querySelector('span');
        this.spinner = this.button?.querySelector('.loading__spinner');
        this.price = this.querySelector('.pups-sticky-atc__price');
        this.options = this.querySelector('.pups-sticky-atc__options');
        this.dynamicCheckout = this.productForm?.closest('product-form')?.querySelector('[data-pups-size-gated-checkout]');
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.requiresSizeChoice = this.dataset.requiresSizeChoice === 'true';
        this.explicitVariantId = new URLSearchParams(window.location.search).get('variant');
        this.sizeChosen = !this.requiresSizeChoice;
        this.initialReconciliationTimers = [];
        this.visibilityFrame = null;

        if (!this.sourceButton || !this.productForm || !this.button) {
          this.hidden = true;
          return;
        }

        this.handleClick = () => {
          if (this.requiresSizeChoice && !this.sizeChosen) {
            this.focusSizeOptions();
            return;
          }

          if (!this.sourceButton.disabled && this.sourceButton.getAttribute('aria-disabled') !== 'true') {
            this.productForm.requestSubmit(this.sourceButton);
          }
        };
        this.handleViewportChange = () => this.scheduleVisibilitySync();
        this.handleMediaChange = () => this.scheduleVisibilitySync();
        this.handleProductInfoLoaded = (event) => {
          if (event.target !== this.productInfo) return;
          this.reconcileExplicitVariantSelection();
          this.observeVisibilitySource();
        };
        this.handleOptionChange = (event) => {
          if (!this.productInfo?.contains(event.target)) return;

          const optionName = this.getOptionName(event.target);
          if (optionName === 'size') {
            this.sizeChosen = Boolean(this.getSelectedSizeValue());
          }

          this.updateOptionSummary();
          this.syncButton();
        };
        this.handleVariantChange = ({ data }) => {
          if (String(data?.sectionId) !== String(this.dataset.sectionId)) return;

          this.refreshReferences();
          if (!this.reconcileExplicitVariantSelection()) {
            if (this.requiresSizeChoice && !this.sizeChosen) this.clearSizeSelection();
            if (this.sizeChosen && data?.variant?.available && !this.sourceButton.disabled) {
              this.sourceButton.removeAttribute('aria-disabled');
            }
            this.dynamicCheckout?.toggleAttribute(
              'hidden',
              this.requiresSizeChoice && (!this.sizeChosen || !data?.variant?.available),
            );
          }
          this.updatePrice(data?.html);
          this.updateOptionSummary();
          this.syncButton();
          this.scheduleVisibilitySync();
        };

        this.button.addEventListener('click', this.handleClick);
        document.addEventListener('change', this.handleOptionChange, true);
        document.addEventListener('product-info:loaded', this.handleProductInfoLoaded);
        this.mobileQuery.addEventListener('change', this.handleMediaChange);
        window.addEventListener('scroll', this.handleViewportChange, { passive: true });
        window.addEventListener('resize', this.handleViewportChange);
        window.visualViewport?.addEventListener('resize', this.handleViewportChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
        }

        this.observeSourceButton();
        this.variantObserver = new MutationObserver((mutations) => {
          const pickerChanged = mutations.some((mutation) =>
            Array.from(mutation.addedNodes).some(
              (node) => node.nodeType === Node.ELEMENT_NODE && (node.matches?.('variant-selects') || node.querySelector?.('variant-selects')),
            ),
          );

          if (!pickerChanged) return;
          this.refreshReferences();
          if (!this.reconcileExplicitVariantSelection() && this.requiresSizeChoice && !this.sizeChosen) {
            this.clearSizeSelection();
          }
          this.updateOptionSummary();
          this.syncButton();
          this.scheduleVisibilitySync();
        });
        if (this.productInfo instanceof Element) {
          this.variantObserver.observe(this.productInfo, { childList: true, subtree: true });
        }

        this.observeVisibilitySource();
        if (!this.reconcileExplicitVariantSelection() && this.requiresSizeChoice) this.clearSizeSelection();
        this.queueInitialReconciliation();
        this.updateOptionSummary();
        this.syncButton();
        this.scheduleVisibilitySync();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.handleClick);
        document.removeEventListener('change', this.handleOptionChange, true);
        document.removeEventListener('product-info:loaded', this.handleProductInfoLoaded);
        this.mobileQuery?.removeEventListener('change', this.handleMediaChange);
        window.removeEventListener('scroll', this.handleViewportChange);
        window.removeEventListener('resize', this.handleViewportChange);
        window.visualViewport?.removeEventListener('resize', this.handleViewportChange);
        this.variantChangeUnsubscriber?.();
        this.sourceObserver?.disconnect();
        this.variantObserver?.disconnect();
        this.visibilityObserver?.disconnect();
        this.initialReconciliationTimers?.forEach((timer) => window.clearTimeout(timer));
        if (this.visibilityFrame !== null) window.cancelAnimationFrame(this.visibilityFrame);
        document.body.classList.remove('pups-sticky-atc-visible');
      }

      queueInitialReconciliation() {
        if (!this.explicitVariantId || !this.requiresSizeChoice) return;

        [0, 50, 150, 400, 900].forEach((delay) => {
          const timer = window.setTimeout(() => {
            this.refreshReferences();
            this.reconcileExplicitVariantSelection();
            this.scheduleVisibilitySync();
          }, delay);
          this.initialReconciliationTimers.push(timer);
        });
      }

      reconcileExplicitVariantSelection() {
        if (!this.explicitVariantId || !this.requiresSizeChoice) return false;

        const selectedVariant = this.getSelectedVariant();
        const variantInput = this.productForm?.querySelector('input[name="id"]');
        const selectedSize = this.getSelectedSizeValue();
        const idsAgree =
          String(selectedVariant?.id || '') === String(this.explicitVariantId) &&
          String(variantInput?.value || '') === String(this.explicitVariantId);

        if (!idsAgree || !selectedVariant?.available || !selectedSize) return false;

        this.sizeChosen = true;
        if (this.sourceButton.disabled) this.sourceButton.disabled = false;
        this.sourceButton.removeAttribute('aria-disabled');

        const sourceLabel = this.sourceButton.querySelector('span');
        const addToCartLabel = window.variantStrings?.addToCart;
        if (sourceLabel && addToCartLabel && sourceLabel.textContent.trim() !== addToCartLabel) {
          sourceLabel.textContent = addToCartLabel;
        }

        this.dynamicCheckout?.removeAttribute('hidden');
        this.updateOptionSummary();
        this.syncButton();
        return true;
      }

      observeSourceButton() {
        this.sourceObserver?.disconnect();
        if (!(this.sourceButton instanceof Element)) return;

        this.sourceObserver = new MutationObserver(() => {
          if (!this.reconcileExplicitVariantSelection() && this.requiresSizeChoice && !this.sizeChosen) {
            this.clearSizeSelection();
          }
          this.updateOptionSummary();
          this.syncButton();
          this.scheduleVisibilitySync();
        });
        this.sourceObserver.observe(this.sourceButton, {
          attributes: true,
          childList: true,
          subtree: true,
        });
      }

      observeVisibilitySource() {
        this.visibilityObserver?.disconnect();
        if (!(this.sourceButton instanceof Element)) return;

        this.visibilityObserver = new IntersectionObserver(
          () => this.scheduleVisibilitySync(),
          { threshold: 0 },
        );
        this.visibilityObserver.observe(this.sourceButton);
        this.syncVisibilityFromViewport();
      }

      scheduleVisibilitySync() {
        if (this.visibilityFrame !== null) return;

        this.visibilityFrame = window.requestAnimationFrame(() => {
          this.visibilityFrame = null;
          this.syncVisibilityFromViewport();
        });
      }

      syncVisibilityFromViewport() {
        if (!(this.sourceButton instanceof Element)) {
          this.hasPassedSource = false;
          this.updateVisibility();
          return;
        }

        this.hasPassedSource = this.sourceButton.getBoundingClientRect().bottom <= 0;
        this.updateVisibility();
      }

      refreshReferences() {
        const sourceButton = document.getElementById(this.dataset.sourceButton);
        const productForm = document.getElementById(this.dataset.productForm);
        if (!(sourceButton instanceof Element) || !(productForm instanceof HTMLFormElement)) return;

        const sourceButtonChanged = sourceButton !== this.sourceButton;
        this.sourceButton = sourceButton;
        this.productForm = productForm;
        this.productInfo = sourceButton.closest('product-info');
        this.dynamicCheckout = productForm.closest('product-form')?.querySelector('[data-pups-size-gated-checkout]');
        if (sourceButtonChanged) {
          this.observeSourceButton();
          this.observeVisibilitySource();
        }
      }

      getVariantPicker() {
        return this.productInfo?.querySelector('variant-selects');
      }

      getSelectedVariant() {
        try {
          return JSON.parse(this.getVariantPicker()?.querySelector('[data-selected-variant]')?.textContent || 'null');
        } catch {
          return null;
        }
      }

      getOptionName(control) {
        if (control.tagName === 'SELECT') {
          return (control.selectedOptions[0]?.dataset.optionName || control.name.replace(/^options\[|\]$/g, '')).toLowerCase();
        }

        return (control.dataset.optionName || '').toLowerCase();
      }

      getSizeControls() {
        const picker = this.getVariantPicker();
        if (!picker) return [];

        return Array.from(picker.querySelectorAll('input[data-option-name], select')).filter(
          (control) => this.getOptionName(control) === 'size',
        );
      }

      getSizeGroup() {
        const control = this.getSizeControls()[0];
        return control?.closest('fieldset, .product-form__input--dropdown');
      }

      getSelectedSizeValue() {
        const controls = this.getSizeControls();
        const radio = controls.find((control) => control.matches('input:checked'));
        if (radio) return radio.value;

        const select = controls.find((control) => control.tagName === 'SELECT');
        return select?.selectedIndex >= 0 ? select.value : '';
      }

      clearSizeSelection() {
        this.getSizeControls().forEach((control) => {
          if (control.tagName === 'SELECT') {
            Array.from(control.options).forEach((option) => option.removeAttribute('selected'));
            control.selectedIndex = -1;
          } else {
            control.checked = false;
          }
        });
      }

      updateOptionSummary() {
        if (!this.options) return;

        const picker = this.getVariantPicker();
        const values = [];
        picker?.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
          const control = group.querySelector('input:checked') || group.querySelector('select');
          if (!control) return;

          const optionName = this.getOptionName(control);
          if (optionName === 'size' && this.requiresSizeChoice && !this.sizeChosen) {
            values.push(this.dataset.chooseSizeLabel);
            return;
          }

          if (control.tagName === 'SELECT' && control.selectedIndex < 0) return;
          values.push(control.value);
        });

        if (this.requiresSizeChoice && !values.includes(this.dataset.chooseSizeLabel) && !this.sizeChosen) {
          values.push(this.dataset.chooseSizeLabel);
        }

        this.options.textContent = values.join(' · ');
      }

      updatePrice(html) {
        if (!this.price || !html) return;

        const priceRoot = html.getElementById(`price-${this.dataset.sectionId}`);
        const priceContainer = priceRoot?.querySelector('.price');
        const priceItem = priceContainer?.classList.contains('price--on-sale')
          ? priceContainer.querySelector('.price__sale .price-item--sale')
          : priceContainer?.querySelector('.price__regular .price-item--regular');
        const priceText = priceItem?.textContent.trim();
        if (priceText) this.price.textContent = priceText;
      }

      focusSizeOptions() {
        const group = this.getSizeGroup();
        if (!group) return;

        group.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusTarget =
          group.querySelector('input:not(.disabled):not(:disabled), select:not(:disabled)') ||
          group.querySelector('input, select');
        window.setTimeout(() => focusTarget?.focus({ preventScroll: true }), 350);
      }

      enforceSizeChoice() {
        const sourceLabel = this.sourceButton.querySelector('span');
        if (!this.sourceButton.disabled) this.sourceButton.disabled = true;
        if (this.sourceButton.getAttribute('aria-disabled') !== 'true') this.sourceButton.setAttribute('aria-disabled', 'true');
        if (sourceLabel && sourceLabel.textContent.trim() !== this.dataset.chooseASizeLabel) {
          sourceLabel.textContent = this.dataset.chooseASizeLabel;
        }

        this.dynamicCheckout?.setAttribute('hidden', '');
        this.button.disabled = false;
        this.button.setAttribute('aria-disabled', 'false');
        this.button.classList.remove('loading');
        if (this.label) this.label.textContent = this.dataset.chooseASizeLabel;
        this.spinner?.classList.add('hidden');
      }

      syncButton() {
        if (this.requiresSizeChoice && !this.sizeChosen) {
          this.enforceSizeChoice();
          return;
        }

        const sourceLabel = this.sourceButton.querySelector('span');
        const isLoading = this.sourceButton.classList.contains('loading');
        const isDisabled = this.sourceButton.disabled || this.sourceButton.getAttribute('aria-disabled') === 'true';

        if (sourceLabel && this.label) this.label.textContent = sourceLabel.textContent.trim();
        this.button.disabled = isDisabled;
        this.button.classList.toggle('loading', isLoading);
        this.button.setAttribute('aria-disabled', String(isDisabled));
        this.spinner?.classList.toggle('hidden', !isLoading);
      }

      updateVisibility() {
        const shouldShow = this.mobileQuery.matches && this.hasPassedSource;
        this.hidden = !shouldShow;
        document.body.classList.toggle('pups-sticky-atc-visible', shouldShow);
      }
    },
  );
}
